import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

/**
 * <Seo> — unified SEO / AEO / GEO metadata block.
 *
 * Two-layered strategy so search + answer engines never miss the metadata:
 *
 * 1. Helmet block         → normal SPA hydration path (client-nav, react-snap, etc.)
 * 2. Imperative useEffect → sets document.title, description, canonical, OG/Twitter
 *                           tags, and JSON-LD directly on the DOM the moment the
 *                           component mounts. Bulletproof against Googlebot rendering
 *                           races where Helmet loses to the initial scrape.
 *
 * JSON-LD schemas passed via `schema` (single object or array) power AEO
 * (Answer Engine — featured snippets, People Also Ask) and GEO (Generative
 * Engine — ChatGPT, Perplexity, Google AI Overviews) by giving models
 * clean, structured facts to quote.
 */
const CANONICAL_HOST = "https://sideb.io";
const DEFAULT_OG_IMAGE = "https://sideb.io/brand/banner-linkedin-1584x396.png";
const DEFAULT_TWITTER_IMAGE = "https://sideb.io/brand/banner-x-1500x500.png";

// Signature attribute lets us find + remove tags we injected on unmount /
// re-render, without disturbing whatever Helmet put in the head.
const SEO_ATTR = "data-sideb-seo";

/** Update-or-create a <meta>. Prefers overwriting an existing tag matching
 *  the selector (even if it's from index.html or Helmet) so we never leave
 *  duplicate description/og/twitter tags behind for crawlers to pick from
 *  at random. Falls back to creating a fresh one tagged with SEO_ATTR. */
const setOrCreateMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(SEO_ATTR, "1");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (v == null) el.removeAttribute(k);
    else el.setAttribute(k, v);
  });
};

/** Same update-or-create semantics for <link rel="…">. */
const setOrCreateLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(SEO_ATTR, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/** Clean up only the JSON-LD scripts we injected. We deliberately DO NOT
 *  remove <meta>/<link> tags on unmount — the next page's Seo component will
 *  overwrite them, and leaving them in place is preferable to briefly having
 *  no meta tags if a scraper hits mid-transition. */
const clearInjectedScripts = () => {
  document.head.querySelectorAll(`script[${SEO_ATTR}]`).forEach((el) => el.remove());
};

export function Seo({
  title,
  description,
  canonical,           // optional override; defaults to CANONICAL_HOST + current pathname
  ogType = "website",  // "website" for hubs, "article" for content pieces
  ogImage,
  twitterImage,
  keywords,            // optional string
  schema,              // JSON-LD: object or array of objects
  noindex = false,     // set true for auth / transactional surfaces
}) {
  const { pathname } = useLocation();
  const canonicalUrl = canonical || `${CANONICAL_HOST}${pathname}`;
  const abs = (u) => (u && u.startsWith("/") ? `${window.location.origin}${u}` : u);
  const image = abs(ogImage) || DEFAULT_OG_IMAGE;
  const tImage = abs(twitterImage || ogImage) || DEFAULT_TWITTER_IMAGE;
  const schemaList = Array.isArray(schema) ? schema : schema ? [schema] : [];

  useEffect(() => {
    // Title + description — imperative fallback for Googlebot's first paint.
    if (title) document.title = title;

    setOrCreateMeta('meta[name="description"]', {
      name: "description",
      content: description || "",
    });

    // Canonical
    setOrCreateLink("canonical", canonicalUrl);

    // Open Graph
    setOrCreateMeta('meta[property="og:title"]', {
      property: "og:title",
      content: title || "",
    });
    setOrCreateMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description || "",
    });
    setOrCreateMeta('meta[property="og:type"]', {
      property: "og:type",
      content: ogType,
    });
    setOrCreateMeta('meta[property="og:url"]', {
      property: "og:url",
      content: canonicalUrl,
    });
    setOrCreateMeta('meta[property="og:image"]', {
      property: "og:image",
      content: image,
    });

    // Twitter
    setOrCreateMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    setOrCreateMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: title || "",
    });
    setOrCreateMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description || "",
    });
    setOrCreateMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: tImage,
    });

    // Keywords (kept for AEO/GEO — LLMs sometimes read them; Google ignores.)
    if (keywords) {
      setOrCreateMeta('meta[name="keywords"]', {
        name: "keywords",
        content: keywords,
      });
    }

    // Robots — noindex for auth/transactional surfaces.
    if (noindex) {
      setOrCreateMeta('meta[name="robots"]', {
        name: "robots",
        content: "noindex, nofollow",
      });
    }

    // JSON-LD structured data. Each schema goes in its own <script type=ld+json>.
    schemaList.forEach((s, idx) => {
      const id = `sideb-jsonld-${idx}`;
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = id;
        el.setAttribute(SEO_ATTR, "1");
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(s);
    });

    return () => {
      clearInjectedScripts();
    };
  }, [title, description, canonicalUrl, ogType, image, tImage, keywords, noindex, JSON.stringify(schemaList)]);

  // Helmet mirror — for react-snap / SSR / non-JS crawlers.
  return (
    <Helmet>
      {title ? <title>{title}</title> : null}
      {description ? <meta name="description" content={description} /> : null}
      <link rel="canonical" href={canonicalUrl} />
      {title ? <meta property="og:title" content={title} /> : null}
      {description ? <meta property="og:description" content={description} /> : null}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      {title ? <meta name="twitter:title" content={title} /> : null}
      {description ? <meta name="twitter:description" content={description} /> : null}
      <meta name="twitter:image" content={tImage} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}
      {schemaList.map((s, idx) => (
        <script key={`ld-${idx}`} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

/* ------------------------------- SCHEMA HELPERS ------------------------------- */

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "SideB",
  alternateName: "sideb.io",
  url: "https://sideb.io",
  logo: "https://sideb.io/brand/banner-linkedin-1584x396.png",
  description:
    "SideB is an AI-First consulting studio and Operating Partner for growth-stage teams. We deliver vendor-agnostic execution playbooks to reduce SaaS bloat, automate manual processes, optimize payment gateways, and build AI-first RevOps pipelines.",
  founder: { "@type": "Person", name: "Sam Wiles" },
  email: "rev@sideb.io",
  sameAs: [
    "https://www.linkedin.com/company/sideb-io",
  ],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "SideB",
  url: "https://sideb.io",
  publisher: { "@type": "Organization", name: "SideB" },
});

export const articleSchema = ({ headline, description, slug, datePublished, dateModified, author = "SideB", image, section }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline,
  description,
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `https://sideb.io/insights/${slug}`,
  },
  author: { "@type": "Organization", name: author, url: "https://sideb.io" },
  publisher: {
    "@type": "Organization",
    name: "SideB",
    logo: {
      "@type": "ImageObject",
      url: "https://sideb.io/brand/banner-linkedin-1584x396.png",
    },
  },
  datePublished,
  dateModified: dateModified || datePublished,
  ...(image ? { image } : {}),
  ...(section ? { articleSection: section } : {}),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const howToSchema = ({ name, description, steps }) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name,
  description,
  step: steps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.title || s.name,
    text: s.summary || s.text || "",
  })),
});

export const serviceSchema = ({ name, description, url, price }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  provider: { "@type": "Organization", name: "SideB", url: "https://sideb.io" },
  areaServed: "Global",
  ...(url ? { url } : {}),
  ...(price ? { offers: { "@type": "Offer", price, priceCurrency: "USD" } } : {}),
});

export const breadcrumbSchema = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: c.url,
  })),
});
